import { useCallback, useEffect } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { setDataAttribute } from '@/utils/dom.utils'

type Hidden = 'hidden' | 'msHidden' | 'webkitHidden'
type VisibilityChange = 'visibilitychange' | 'msvisibilitychange' | 'webkitvisibilitychange'

type DocumentWithHidden = {
  msHidden?: boolean
  webkitHidden?: boolean
} & Document

export const DATA_ATTR_NAME = 'data-visible'

/**
 * Get the browser visibility property name for the browser
 * @returns {VisibilityChange}
 */
export function getBrowserVisibilityProp(): VisibilityChange | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    return 'visibilitychange'
  }
  if (typeof (document as DocumentWithHidden).msHidden !== 'undefined') {
    return 'msvisibilitychange'
  }
  if (typeof (document as DocumentWithHidden).webkitHidden !== 'undefined') {
    return 'webkitvisibilitychange'
  }
  return undefined
}

/**
 * Get the document hidden property name for the browser
 * @returns {Hidden}
 */
export function getBrowserDocumentHiddenProp(): Hidden | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  if (typeof document.hidden !== 'undefined') {
    return 'hidden'
  }
  if (typeof (document as DocumentWithHidden).msHidden !== 'undefined') {
    return 'msHidden'
  }
  if (typeof (document as DocumentWithHidden).webkitHidden !== 'undefined') {
    return 'webkitHidden'
  }
  return undefined
}

/**
 * Determine whether the document is visible using the
 * browser document hidden prop
 * @returns {boolean}
 */
export function getIsDocumentVisible(): boolean {
  if (typeof document === 'undefined') {
    return true
  }
  const hiddenProp = getBrowserDocumentHiddenProp()
  if (hiddenProp) {
    return !(document as DocumentWithHidden)[hiddenProp as keyof DocumentWithHidden]
  }
  return false
}

/**
 * This hook updates a reactive variable when the tab loses and gains focus
 */
const usePageVisibility = (): void => {
  const setSiteIsVisible = useOurStore((state) => state.setSiteIsVisible)
  // on visibility change callback
  const onVisibilityChange = useCallback((): void => {
    const isVisible = getIsDocumentVisible()
    if (typeof document !== 'undefined') {
      setDataAttribute(document.body, DATA_ATTR_NAME, isVisible ? 'true' : 'false')
    }
    setSiteIsVisible(isVisible)
  }, [setSiteIsVisible])

  useEffect(() => {
    const visibilityChange = getBrowserVisibilityProp()
    // first run
    onVisibilityChange()

    if (visibilityChange) {
      document.addEventListener(visibilityChange, onVisibilityChange, false)
    }

    return () => {
      if (visibilityChange) {
        document.removeEventListener(visibilityChange, onVisibilityChange)
      }
    }
  }, [onVisibilityChange])
}

export default usePageVisibility
